import React, { type ReactNode } from 'react'
import { OnboardingContactMappingCategory } from '../../../common/integrations'
import { WarningAlert } from '../PostConnect/Components/WarningAlert'

export interface RadioOptionCopy {
  label: string
  description: ReactNode
}

export interface RadioControlCopy {
  off: RadioOptionCopy
  on: RadioOptionCopy
  onWarning?: ReactNode
  dualPrimaryLabel: string
  dualSecondaryLabel: string
  singleActionLabel: string
  default: boolean
}

export interface ToggleControlCopy {
  syncOnDescription: (isEnterprise: boolean) => ReactNode
  buttonLabel: string
  default?: boolean
}

export interface ContactSyncCategoryCopy {
  radio?: RadioControlCopy
  toggle?: ToggleControlCopy
  showRadioForAllUsers?: true
}

export const CONTACT_SYNC_COPY: Record<OnboardingContactMappingCategory, ContactSyncCategoryCopy> = {
  [OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS]: {
    radio: {
      off: {
        label: 'Set up default contact mapping first for your Account',
        description: (
          <>
            <strong>No contacts will be synced</strong> until you set up mapping in Integrations.
          </>
        ),
      },
      on: {
        label: 'Use Zocks default settings for your Account',
        description: (
          <>
            Apply contact mapping <strong>set by Zocks</strong>, and import your contacts
          </>
        ),
      },
      dualPrimaryLabel: 'Set up contact mapping',
      dualSecondaryLabel: 'Sync contacts later',
      singleActionLabel: 'Sync contacts',
      default: false,
    },
    toggle: {
      syncOnDescription: (isEnterprise) => (
        <>
          Contacts will be synced based on <strong>{isEnterprise ? 'Enterprise defaults' : 'Account defaults'}</strong>
        </>
      ),
      buttonLabel: 'Next',
      default: true,
    },
  },
  [OnboardingContactMappingCategory.NO_DEFAULT_FILTERS]: {
    showRadioForAllUsers: true,
    radio: {
      off: {
        label: 'Set up contact filters first',
        description: (
          <>
            <strong>No contacts will be synced</strong> until you set up filters in Integrations.
          </>
        ),
      },
      on: {
        label: 'Sync all of your contacts now',
        description: (
          <>
            Import all contacts <strong>without filtering</strong> you have access to
          </>
        ),
      },
      onWarning: <WarningAlert>Cannot filter contacts later once imported. You can delete them manually.</WarningAlert>,
      dualPrimaryLabel: 'Set up contact filters',
      dualSecondaryLabel: 'Sync contacts later',
      singleActionLabel: 'Sync all contacts',
      default: false,
    },
  },
  [OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER]: {
    toggle: {
      syncOnDescription: () => (
        <>
          Import <strong>all contacts</strong> you have access to
        </>
      ),
      buttonLabel: 'Next',
      default: true,
    },
  },
}
